import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Container,
  Col,
  Form,
  Row,
  Alert,
  Spinner,
  Button,
  Badge,
  ListGroup,
  InputGroup,
} from "react-bootstrap";
import axios from "axios";
import { Navigate, useLocation } from "react-router-dom"; // Added useLocation
import Swal from "sweetalert2";
import {
  Send,
  Clock,
  FileText,
  Users,
  Wallet,
  BookUser,
  PlusCircle,
  Search,
  Save, // Added Save icon
} from "lucide-react";

import Header from "./Header";
import Footer from "./Footer";
import SideMenu from "./SideMenu";
import MemberMenu from "./MemberMenu";
import AdminMenu from "./AdminMenu";
import Logout from "./Logout";
import { useAuth } from "./Home1";

const processRecipients = (recipientInput) => {
  if (!recipientInput || typeof recipientInput !== "string") {
    return {
      validNumbersString: "",
      validCount: 0,
      invalidCount: 0,
      duplicateCount: 0,
    };
  }
  const rawNumbers = recipientInput
    .replace(/\s+/g, ",")
    .split(",")
    .filter(Boolean);
  const uniqueNumbers = [...new Set(rawNumbers)];
  const duplicateCount = rawNumbers.length - uniqueNumbers.length;

  let invalidCount = 0;
  const validNumbers = uniqueNumbers
    .map((num) => {
      const trimmedNum = num.trim();
      if (trimmedNum.startsWith("0") && trimmedNum.length === 11)
        return `234${trimmedNum.substring(1)}`;
      if (trimmedNum.startsWith("234") && trimmedNum.length === 13)
        return trimmedNum;
      invalidCount++;
      return null;
    })
    .filter(Boolean);

  return {
    validNumbersString: validNumbers.join(","),
    validCount: validNumbers.length,
    invalidCount,
    duplicateCount,
  };
};

const BEACONSMS_API_BASE = "https://beaconsms.com/oldsite";
const GET_USER_DETAILS_API = `${BEACONSMS_API_BASE}/index.php/api/v1/mini/getuserdetails`;
const SEND_TRANSACTIONAL_API = `${BEACONSMS_API_BASE}/api/transactional_send.php`;
const PHONEBOOK_API = `${BEACONSMS_API_BASE}/api/phonebook_api.php`;
const DRAFT_API = `${BEACONSMS_API_BASE}/api/drafts_api.php`; // Added Draft API
const SMS_SEGMENT_LENGTH = 155;

const BulkSms = () => {
  const { user, isLoggedIn } = useAuth();
  const fileInputRef = useRef(null);
  const location = useLocation(); // Hook to access state passed from Drafts page
  const currentUserId = user?.id;

  const [formData, setFormData] = useState({
    senderId: "",
    recipientInput: "",
    message: "",
    recipientSource: "",
    isScheduled: false,
    scheduleDate: "",
  });

  const [uiState, setUiState] = useState({
    isSending: false,
    isSavingDraft: false, // Track draft saving state
    statusMessage: "",
    errorMessage: "",
    messageCharCount: 0,
    messagePageCount: 0,
    showRecipientBox: false,
    showFileInput: false,
    showPhonebook: false,
    isLoadingBalance: true,
    currentBalance: null,
    searchTerm: "", 
  });

  const [contacts, setContacts] = useState([]);
  const [selectedGroupIds, setSelectedGroupIds] = useState([]);

  // 1. Logic to catch data from the Drafts page
  useEffect(() => {
    if (location.state) {
      const { sender, recipients, message } = location.state;
      
      // Update form data
      setFormData((prev) => ({
        ...prev,
        senderId: sender || "",
        recipientInput: recipients || "",
        message: message || "",
        recipientSource: recipients ? "paste" : "",
      }));

      // Update character counts for the loaded message
      if (message) {
        const count = message.length;
        const pages = count === 0 ? 0 : Math.ceil(count / SMS_SEGMENT_LENGTH) || 1;
        setUiState((prev) => ({
          ...prev,
          messageCharCount: count,
          messagePageCount: pages,
          showRecipientBox: !!recipients,
        }));
      }

      // Clear the state from history so it doesn't reload on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location]);

  const fetchBalance = useCallback(async () => {
    if (!currentUserId) return;
    setUiState((prev) => ({ ...prev, isLoadingBalance: true }));
    try {
      const response = await axios.get(
        `${GET_USER_DETAILS_API}?user_id=${currentUserId}`,
      );
      const fetchedBalance = parseFloat(response.data?.data?.[0]?.wallet);
      setUiState((prev) => ({
        ...prev,
        currentBalance: isNaN(fetchedBalance) ? null : fetchedBalance,
        isLoadingBalance: false,
      }));
    } catch (error) {
      setUiState((prev) => ({ ...prev, isLoadingBalance: false }));
    }
  }, [currentUserId]);

  const fetchPhonebook = useCallback(async () => {
    if (!currentUserId) return;
    try {
      const response = await axios.get(
        `${PHONEBOOK_API}?user_id=${currentUserId}`,
      );
      setContacts(response.data || []);
    } catch (error) {
      console.error("Phonebook error", error);
    }
  }, [currentUserId]);

  useEffect(() => {
    if (currentUserId) fetchBalance();
  }, [currentUserId, fetchBalance]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    const finalValue = type === "checkbox" ? checked : value;
    setFormData((prev) => ({ ...prev, [name]: finalValue }));
    if (name === "message") {
      const count = value.length;
      const pages =
        count === 0 ? 0 : Math.ceil(count / SMS_SEGMENT_LENGTH) || 1;
      setUiState((prev) => ({
        ...prev,
        messageCharCount: count,
        messagePageCount: pages,
      }));
    }
  };

  const handleSourceChange = (e) => {
    const source = e.target.value;
    setFormData((prev) => ({
      ...prev,
      recipientSource: source,
      recipientInput: "",
    }));
    setSelectedGroupIds([]);
    setUiState((prev) => ({
      ...prev,
      showRecipientBox: source === "paste",
      showFileInput: source === "file",
      showPhonebook: source === "phonebook",
      searchTerm: "",
    }));
    if (source === "phonebook") fetchPhonebook();
  };

  const handleGroupToggle = (groupId, groupNumbers) => {
    let updatedIds = [...selectedGroupIds];
    let currentRecipients = formData.recipientInput
      ? formData.recipientInput.split(",")
      : [];
    const newNumbers = groupNumbers.split(",");

    if (updatedIds.includes(groupId)) {
      updatedIds = updatedIds.filter((id) => id !== groupId);
      currentRecipients = currentRecipients.filter(
        (num) => !newNumbers.includes(num),
      );
    } else {
      updatedIds.push(groupId);
      currentRecipients = [...new Set([...currentRecipients, ...newNumbers])];
    }

    setSelectedGroupIds(updatedIds);
    setFormData((prev) => ({
      ...prev,
      recipientInput: currentRecipients.join(","),
    }));
  };

  const filteredContacts = contacts.filter((c) =>
    c.contact_name.toLowerCase().includes(uiState.searchTerm.toLowerCase()),
  );

  // 2. Function to save message as Draft
  const handleSaveDraft = async () => {
    if (!formData.message || !formData.senderId) {
      Swal.fire("Wait", "Enter a Sender ID and Message to save a draft.", "info");
      return;
    }

    setUiState((prev) => ({ ...prev, isSavingDraft: true }));
    try {
      const payload = {
        action: "save_draft",
        userId: currentUserId,
        sender_id: formData.senderId,
        recipients: formData.recipientInput,
        message: formData.message,
      };

      const res = await axios.post(DRAFT_API, payload);
      if (res.data.status === "success") {
        Swal.fire("Success", "Message saved to drafts!", "success");
      } else {
        throw new Error(res.data.message);
      }
    } catch (err) {
      Swal.fire("Error", "Failed to save draft.", "error");
    } finally {
      setUiState((prev) => ({ ...prev, isSavingDraft: false }));
    }
  };

  const handleSaveToPhonebook = () => {
    if (!formData.recipientInput) {
      Swal.fire("Error", "No numbers found to save.", "error");
      return;
    }
    Swal.fire({
      title: "Save as Group/Contact",
      input: "text",
      inputLabel: "Group Name",
      showCancelButton: true,
    }).then(async (result) => {
      if (result.isConfirmed && result.value) {
        try {
          await axios.post(PHONEBOOK_API, {
            userId: currentUserId,
            name: result.value,
            phone: formData.recipientInput,
          });
          Swal.fire("Saved!", "Group added.", "success");
          if (uiState.showPhonebook) fetchPhonebook();
        } catch (err) {
          Swal.fire("Error", "Save failed.", "error");
        }
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setUiState((prev) => ({ ...prev, isSending: true, errorMessage: "" }));
    const { validNumbersString, validCount } = processRecipients(
      formData.recipientInput,
    );
    if (validCount === 0) {
      setUiState((prev) => ({
        ...prev,
        isSending: false,
        errorMessage: "No valid numbers.",
      }));
      return;
    }
    try {
      const payload = {
        userId: currentUserId,
        senderId: formData.senderId,
        recipients: validNumbersString,
        message: formData.message,
        schedule_date: formData.isScheduled
          ? formData.scheduleDate.replace("T", " ") + ":00"
          : null,
      };
      const res = await axios.post(SEND_TRANSACTIONAL_API, payload);
      Swal.fire("Success", res.data.message, "success");
      setUiState((prev) => ({
        ...prev,
        isSending: false,
        currentBalance: res.data.newBalance,
      }));
      setFormData({
        senderId: "",
        recipientInput: "",
        message: "",
        recipientSource: "",
        isScheduled: false,
        scheduleDate: "",
      });
      setSelectedGroupIds([]);
    } catch (err) {
      setUiState((prev) => ({
        ...prev,
        isSending: false,
        errorMessage: err.response?.data?.message || "Failed",
      }));
    }
  };

  if (!isLoggedIn) return <Navigate to='/login' replace />;

  return (
    <Container className='pb-5'>
      <Header />
      <Row className='mt-4'>
        <Col md={3}>
          <Logout Menu='Login Menu' user={user?.username} />
          <SideMenu Menu="Member's Menu" menuu={MemberMenu} />
          {user?.id === "71" && (
            <SideMenu Menu="Admin's Menu" menuu={AdminMenu} />
          )}
        </Col>

        <Col md={9}>
          <div className='d-flex justify-content-between align-items-center mb-4 p-3 bg-white border rounded shadow-sm'>
            <div className='d-flex align-items-center'>
              <div className='bg-primary-subtle rounded-3 p-2 me-3 border'>
                <Wallet className='text-primary' size={24} />
              </div>
              <div>
                <small
                  className='text-muted text-uppercase fw-bold'
                  style={{ fontSize: "10px" }}
                >
                  Units Available
                </small>
                <h4 className='mb-0 fw-bold'>
                  {uiState.isLoadingBalance ? (
                    <Spinner size='sm' />
                  ) : (
                    `${Number(uiState.currentBalance || 0).toLocaleString()} units`
                  )}
                </h4>
              </div>
            </div>
            <Badge
              bg={uiState.currentBalance < 50 ? "danger" : "success"}
              pill
              className='px-3 py-2'
            >
              {uiState.currentBalance < 50 ? "Low Units" : "Active"}
            </Badge>
          </div>

          <Form
            onSubmit={handleSubmit}
            className='bg-white p-4 border rounded shadow-sm'
          >
            <div className='d-flex align-items-center mb-4 border-bottom pb-2'>
              <Send className='text-primary me-2' size={20} />
              <h5 className='mb-0 fw-bold'>Compose Bulk SMS</h5>
            </div>

            {uiState.errorMessage && (
              <Alert variant='danger'>{uiState.errorMessage}</Alert>
            )}

            <Row className='mb-3'>
              <Col md={6}>
                <Form.Label className='small fw-bold'>Sender ID</Form.Label>
                <Form.Control
                  type='text'
                  name='senderId'
                  value={formData.senderId}
                  maxLength={11}
                  required
                  onChange={handleInputChange}
                  disabled={uiState.isSending}
                  placeholder='Max. character is 11'
                />
              </Col>
              <Col md={6}>
                <Form.Label className='small fw-bold'>
                  Recipient Source
                </Form.Label>
                <Form.Select
                  name='recipientSource'
                  value={formData.recipientSource}
                  onChange={handleSourceChange}
                  required
                  disabled={uiState.isSending}
                >
                  <option value=''>-- Choose Source --</option>
                  <option value='file'>Upload File</option>
                  <option value='paste'>Paste Numbers / Group</option>
                  <option value='phonebook'>Saved Groups/Contacts</option>
                </Form.Select>
              </Col>
            </Row>

            {uiState.showRecipientBox && (
              <Form.Group className='mb-3'>
                <Form.Control
                  as='textarea'
                  name='recipientInput'
                  value={formData.recipientInput}
                  rows={3}
                  placeholder='Comma or line separated...'
                  onChange={handleInputChange}
                  required
                />
                <Button
                  variant='link'
                  size='sm'
                  className='p-0 mt-1 text-decoration-none'
                  onClick={handleSaveToPhonebook}
                >
                  <PlusCircle size={14} className='me-1' /> Save as Group
                </Button>
              </Form.Group>
            )}

            {uiState.showPhonebook && (
              <div className='mb-3'>
                <InputGroup size='sm' className='mb-2'>
                  <InputGroup.Text className='bg-white'>
                    <Search size={14} />
                  </InputGroup.Text>
                  <Form.Control
                    placeholder='Search groups...'
                    value={uiState.searchTerm}
                    onChange={(e) =>
                      setUiState((prev) => ({
                        ...prev,
                        searchTerm: e.target.value,
                      }))
                    }
                  />
                </InputGroup>
                <ListGroup
                  className='shadow-sm border overflow-auto'
                  style={{ maxHeight: "180px" }}
                >
                  {filteredContacts.length > 0 ? (
                    filteredContacts.map((c) => (
                      <ListGroup.Item key={c.id} className='py-2'>
                        <Form.Check
                          type='checkbox'
                          label={
                            <span>
                              <strong>{c.contact_name}</strong>{" "}
                              <small className='text-muted ms-2'>
                                ({c.phone_number.split(",").length} numbers)
                              </small>
                            </span>
                          }
                          checked={selectedGroupIds.includes(c.id)}
                          onChange={() =>
                            handleGroupToggle(c.id, c.phone_number)
                          }
                        />
                      </ListGroup.Item>
                    ))
                  ) : (
                    <ListGroup.Item className='small text-muted text-center'>
                      No matching groups found.
                    </ListGroup.Item>
                  )}
                </ListGroup>
              </div>
            )}

            {uiState.showFileInput && (
              <Form.Control
                type='file'
                accept='.csv, .txt'
                ref={fileInputRef}
                className='mb-3'
                onChange={(e) => {
                  if(!e.target.files[0]) return;
                  const reader = new FileReader();
                  reader.onload = (ev) =>
                    setFormData((p) => ({
                      ...p,
                      recipientInput: ev.target.result,
                    }));
                  reader.readAsText(e.target.files[0]);
                }}
                required
              />
            )}

            <Form.Group className='mb-3'>
              <Form.Label className='small fw-bold'>Message</Form.Label>
              <Form.Control
                as='textarea'
                name='message'
                value={formData.message}
                rows={4}
                maxLength={640}
                onChange={handleInputChange}
                required
                disabled={uiState.isSending}
              />
              <div className='d-flex justify-content-between mt-1 small'>
                <span className='text-primary fw-bold'>
                  Pages: {uiState.messagePageCount}
                </span>
                <span className='text-muted'>
                  Chars: {uiState.messageCharCount}
                </span>
              </div>
            </Form.Group>

            <div
              className={`p-3 rounded border mb-4 ${formData.isScheduled ? "bg-light border-primary" : "bg-white"}`}
            >
              <Form.Check
                type='switch'
                id='sc-sw'
                label='Schedule this message?'
                name='isScheduled'
                checked={formData.isScheduled}
                onChange={handleInputChange}
              />
              {formData.isScheduled && (
                <Form.Control
                  type='datetime-local'
                  className='mt-2'
                  name='scheduleDate'
                  value={formData.scheduleDate}
                  min={new Date().toISOString().slice(0, 16)}
                  onChange={handleInputChange}
                  required
                />
              )}
            </div>

            {/* 3. Action Buttons Row */}
            <Row className="g-2">
              <Col md={8}>
                <Button
                  variant='primary'
                  type='submit'
                  className='w-100 py-2 fw-bold'
                  disabled={uiState.isSending || uiState.isLoadingBalance || uiState.isSavingDraft}
                >
                  {uiState.isSending ? (
                    <Spinner size='sm' />
                  ) : formData.isScheduled ? (
                    "Schedule Broadcast"
                  ) : (
                    "Send Now"
                  )}
                </Button>
              </Col>
              <Col md={4}>
                <Button
                  variant='outline-secondary'
                  className='w-100 py-2 fw-bold'
                  onClick={handleSaveDraft}
                  disabled={uiState.isSending || uiState.isSavingDraft}
                >
                  {uiState.isSavingDraft ? (
                    <Spinner size='sm' />
                  ) : (
                    <><Save size={18} className="me-1" /> Draft</>
                  )}
                </Button>
              </Col>
            </Row>

          </Form>
        </Col>
      </Row>
      <Footer />
    </Container>
  );
};

export default BulkSms;